import { getPickedEmployee } from '@/helpers/employee';
import { getSelectionPricing } from '@/helpers/getSelectionPricing';
import { getServiceCampaignIfAny, getServiceDuration, getServicePrice, getTotalPrice } from '@/helpers/placeHelper';
import { servicesWithExtraTimeAddOns } from '@/helpers/service';
import { _t } from '@/locale';
import { BookCampaign, BookState } from '@/types/state/book';
import { Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { SelectionSummaryItem } from './components/SelectionSummary.types';
import { resolvePriceListId } from './serviceItem.helpers';

export type BuildSelectionSummaryArgs = {
  pickedServices: PlaceService[];
  place: Place;
  priceListId: string | number;
  campaigns: BookCampaign[];
};

export type BuildSelectionSummaryResult = {
  items: SelectionSummaryItem[];
  total?: string;
};

export type BookSelectionSummary = BuildSelectionSummaryResult & {
  pickedEmployee?: PlaceEmployee;
  priceListId: string | number;
};

/**
 * The summary of a hydrated `state.book` selection: rows and total, plus the picked
 * employee and price list they were priced through.
 *
 * The step pages render this next to the SelectionsBar, which totals the same
 * selection through `getPickedEmployee` → `resolvePriceListId` (useSelectionsBar).
 * Resolving the price list any other way would put two different totals on one
 * screen, so the pages take the derivation from here rather than repeating it.
 */
export function buildBookSelectionSummary(
  book: Pick<BookState, 'services' | 'employee' | 'campaigns'>,
  place: Place,
): BookSelectionSummary {
  const pickedEmployee = book.employee != null ? getPickedEmployee(book.employee, place.employees ?? []) : undefined;
  const priceListId = resolvePriceListId(pickedEmployee);
  const summary = buildSelectionSummary({
    pickedServices: book.services ?? [],
    place,
    priceListId,
    campaigns: book.campaigns ?? [],
  });

  return { ...summary, pickedEmployee, priceListId };
}

/**
 * Derives the SelectionSummary rows and total from a picked selection — the display
 * layer shared by the step pages.
 *
 * `priceListId` is resolved by the caller rather than here, so a caller that renders
 * this alongside the SelectionsBar has to hand both the same value — the two totals
 * are the same selection counted twice, and the bar resolves it with
 * `resolvePriceListId`.
 */
export function buildSelectionSummary({
  pickedServices,
  place,
  priceListId,
  campaigns,
}: BuildSelectionSummaryArgs): BuildSelectionSummaryResult {
  const pricing = getSelectionPricing(pickedServices, place, priceListId, campaigns);
  // Mirrors useSelectionsBar's "from"-prefix for variable prices.
  const total = pricing.shouldShowPrices
    ? (pricing.isPriceVariable && pricing.total ? `${_t('from')} ${pricing.total}` : pricing.total) || undefined
    : undefined;
  // Extra-time add-ons get no row of their own — their price and duration fold into the
  // owning main service's row, as on the confirmation page (mapConfirmedBookingInfoServices).
  const { services: summaryServices, mapServiceWithExtraTimeAddOns } = servicesWithExtraTimeAddOns(pickedServices);
  const pickedServiceIds = pickedServices.map((s) => s.id);
  // A main service is removable only while a second one keeps the selection non-empty.
  const pickedMainServiceCount = summaryServices.filter((service) => !service.about?.settings?.isAddOn).length;
  const items = summaryServices.map((service) => {
    const extraTimeAddOn = mapServiceWithExtraTimeAddOns[service.id];
    // Campaign-aware row price with the pre-campaign price struck through, as on the
    // checkout rows (CheckoutDetails) and mapConfirmedBookingInfoServices.
    const { campaignService } = getServiceCampaignIfAny(service, campaigns, pickedServiceIds);
    const price = extraTimeAddOn
      ? getTotalPrice([service, extraTimeAddOn], place, priceListId, {}, 0, 1, true, campaigns)
      : getServicePrice(service, priceListId, place, campaignService);
    const priceWithoutCampaigns = extraTimeAddOn
      ? getTotalPrice([service, extraTimeAddOn], place, priceListId, {})
      : getServicePrice(service, priceListId, place);
    return {
      id: service.id,
      name: service.name ?? '',
      duration: getServiceDuration(service, priceListId, undefined, extraTimeAddOn) || undefined,
      price: price ? String(price) : undefined,
      originalPrice:
        price && priceWithoutCampaigns && String(price) !== String(priceWithoutCampaigns)
          ? String(priceWithoutCampaigns)
          : undefined,
      removable: Boolean(service.about?.settings?.isAddOn) || pickedMainServiceCount > 1,
    };
  });

  // Campaign products (a brand deal's bundled goods) are part of the selection the total is
  // summed over, so they need rows of their own. Read off every campaign, the same basket
  // getSelectionPricing charges for, so a listed row and a charged item can never diverge.
  // They carry no id, no duration and are not individually removable — the campaign owns them.
  const productItems = (campaigns ?? []).flatMap((campaign, campaignIndex) =>
    (campaign.products ?? []).map((product, productIndex) => ({
      id: `product-${campaignIndex}-${productIndex}`,
      name: product.name ?? '',
      price: pricing.shouldShowPrices ? product.priceLabel : undefined,
      originalPrice:
        pricing.shouldShowPrices && product.originalPriceLabel && product.originalPriceLabel !== product.priceLabel
          ? product.originalPriceLabel
          : undefined,
      removable: false,
    })),
  );

  return { items: [...items, ...productItems], total };
}

/**
 * The extra-time add-ons in `remainingServices` that no main service there claims — the
 * inverse of the folding buildSelectionSummary does above.
 *
 * An extra-time add-on has no row of its own; its price and duration are shown on the row of
 * the main service that lists it. Once that main service is gone the add-on is invisible but
 * still priced and still booked, so the caller that removed it has to pop these too. Two main
 * services can list the same extra-time add-on and the flow appends it once, which is why
 * ownership is resolved over what remains rather than off the removed service.
 */
export function getOrphanedExtraTimeAddOnIds(remainingServices: PlaceService[]): number[] {
  const { extraTimeAddonServices, mapServiceWithExtraTimeAddOns } = servicesWithExtraTimeAddOns(remainingServices);
  const owned = new Set(Object.values(mapServiceWithExtraTimeAddOns).map((addOn) => addOn.id));

  return extraTimeAddonServices.filter((addOn) => !owned.has(addOn.id)).map((addOn) => addOn.id);
}
